import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import Button from "../../../components/base-components/ButtonBase";
import Input from "../../../components/base-components/Input";
import { AppDispatch, RootState } from "../../../store";
import { authenticateDevelopmentUpdates } from "../../../store/slices/developmentUpdatesSlice";
import Loader from "../../../components/base-components/Loader";

const AuthenticationForm: React.FC = () => {
  const [ref, inView] = useInView({ threshold: 0.2 });
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { projectId: projectIdFromUrl } = useParams<{ projectId?: string }>();
  const { status, error } = useSelector(
    (state: RootState) => state.developmentUpdates
  );
  const [plotNumber, setPlotNumber] = useState("");
  const [password, setPassword] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!plotNumber.trim()) {
      setLocalError("Plot Number is required");
      return;
    }

    if (!password.trim()) {
      setLocalError("Password is required");
      return;
    }

    if (!projectIdFromUrl) {
      setLocalError("Project ID is missing. Please access this page from a project details page.");
      return;
    }

    const projId = parseInt(projectIdFromUrl, 10);
    if (isNaN(projId)) {
      setLocalError("Project ID is invalid");
      return;
    }

    try {
      const result = await dispatch(
        authenticateDevelopmentUpdates({
          PlotNumber: plotNumber.trim(), // String, not converted to number
          password: password.trim(),
          projectId: projId,
        })
      ).unwrap();

      if (result && result.data && result.data.length > 0) {
        // Navigate to development updates view with projectId in URL
        navigate(`/development-updates/view/${projId}`, { replace: true });
      } else {
        setLocalError("No development updates found");
      }
    } catch (err: any) {
      // Error is handled by Redux, but we also show it locally
      const errorMessage =
        typeof err === "string" ? err : err?.message || error || "Authentication failed. Please try again.";
      setLocalError(errorMessage);
    }
  };

  const displayError = localError || error;

  return (
    <motion.div
      ref={ref}
      className="flex items-start justify-center px-4"
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      transition={{ duration: 0.8, ease: "easeInOut" }}
    >
      <div className="w-full max-w-[50rem]">
        {status === "loading" ? (
          <div className="flex items-center justify-center py-20">
            <Loader />
          </div>
        ) : (
          <form
            className="w-full flex flex-col bg-white border border-gray-200 rounded-[0.5rem] shadow-md p-8"
            onSubmit={handleSubmit}
          >
            {displayError && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-[0.5rem]">
                <p className="text-rose-500 text-center">{displayError}</p>
              </div>
            )}
          <Input
            topLabel="Plot Number"
            name="plotNumber"
            type="text"
            placeholder="Enter your plot number"
            value={plotNumber}
            onChange={(e) => setPlotNumber(e.target.value)}
            required
          />
          <Input
            type="password"
            topLabel="Password"
            name="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
            <Button type="submit">
              <p>Submit</p>
            </Button>
          </form>
        )}
      </div>
    </motion.div>
  );
};

export default AuthenticationForm;
